import debounce from "../lib/debouncer";
/* import './accessible-combobox.pcss'; */

export type AccessibleComboboxOptions = {
	suggestionsFetcher: SuggestionsFetcher, suggestionsResponseParser: SuggestionsResponseParser,
	allowCustomValue?: boolean
}
export type SuggestionsFetcher = (input: string, signal: AbortController['signal']) => ReturnType<typeof fetch>;
export type SuggestionsResponseParser = (response: Response) => Promise<Array<{label: string|HTMLElement, value: string}>>
export class AccessibleCombobox {
	private readonly allowCustomValue: boolean;

	private abortController?: AbortController;
	private spinnerDisplayTimeout?: ReturnType<typeof setTimeout>;
	private afterFocusOutCallbacks: (() => void)[] = [];

	private constructor(
		private readonly container: HTMLElement,
		private readonly input: HTMLInputElement,
		private readonly resultsList: HTMLElement,
		private readonly resultsCountDisplay: HTMLElement,
		{suggestionsFetcher, suggestionsResponseParser, allowCustomValue = false}: AccessibleComboboxOptions
	) {
		this.allowCustomValue = allowCustomValue;
		const search = debounce((query: string) => {
			this.cancelActiveRequest();

			if (query.length <= 1) {
				this.expanded = false;
			} else {
				this.container.classList.toggle("loading", true);
				const abortController = new AbortController();
				this.abortController = abortController;
				this.startWaitingForResults();

				suggestionsFetcher(this.input.value, abortController.signal)
					.then(response => suggestionsResponseParser(response))
					.then(suggestions => {
						if (abortController.signal.aborted) {
							throw "NEW_REQUEST";
						}
						if (!suggestions.length) {
							this.expanded = false;
							this.resultsList.innerHTML = "";
							this.clearWaitingForResultsSpinner();
							return;
						}
						const options: HTMLLIElement[] = [];
						for (let i = 0; i < suggestions.length; i++){
							const {label, value} = suggestions[i];
							const option = document.createElement('li');
							option.setAttribute('role', 'option');
							option.setAttribute("data-value", value);
							option.append(label);
							options.push(option);

							// Move visual indicator as mouse users hover over option
							option.addEventListener('mouseover', () => {
								this.removeHighlight();
								this.highlightOption(option);
							});
							option.addEventListener('click', () => {
								this.selectOption(option);
							})
						}
						this.resultsList.replaceChildren(...options);

						// Announce the number of results found to screen reader users using the ARIA live region.
						if (options.length === 0) {
							this.resultsCountDisplay.innerHTML = "No results found.";
						} else if (options.length === 1) {
							this.resultsCountDisplay.innerHTML = "One result found.";
						} else {
							this.resultsCountDisplay.innerHTML = `${options.length} results found.`;
						}

						// We only want to show the results list when it is relevant
						this.expanded = suggestions.length !== 1 || suggestions[0].value !== query;
						this.clearWaitingForResultsSpinner();
					})
					.catch(err => {
						if (err !== "NEW_REQUEST" && !abortController.signal.aborted) {
							console.error(err);
							this.clearWaitingForResultsSpinner();
						}
					});
			}
		}, 250, false);
		input.addEventListener('keypress', e => {
			if (e.key === 'Enter' && this.expanded) {
				e.preventDefault();
				e.stopPropagation();
				return false;
			}
		});
		input.addEventListener('keyup', e => {
			switch(e.key) {
				case 'ArrowUp':
					e.preventDefault();
					if (this.expanded) {
						this.highlightPreviousOption();
					} else {
						search.runNow(this.input.value);
						if (this.resultsList.children.length > 0) {
							this.expanded = true;
						}
					}
					break;
				case 'ArrowDown':
					e.preventDefault();
					if (this.expanded) {
						this.highlightNextOption();
					} else {
						search.runNow(this.input.value);
						if (this.resultsList.children.length > 0) {
							this.expanded = true;
						}
					}
					break;
				case 'Escape':
					if (this.expanded) {
						this.expanded = false;
					}
					break;
				case 'Enter':
					if (this.expanded) {
						e.preventDefault();
						e.stopPropagation();
						this.selectOption(container.querySelector<HTMLElement>('[aria-selected="true"]') ?? container.querySelector<HTMLElement>("li:first-of-type"));
						this.expanded = false;
						return false;
					}
					break;
				default:
					// Only alphanumeric keys, Backspace, Delete, Tab, and some special characters (_, +, and -)
					if (/^[\b\t.a-zA-Z0-9_+-]*$/.test(e.key)) {
						search(this.input.value);
					}
					break;
			}
		});
		input.addEventListener('focus', () => {
			if (input.value !== '') {
				if (resultsList.children.length === 1) {
					if (this.input.value !== resultsList.children[0].getAttribute("data-value")) {
						this.expanded = true;
					}
				} else if (resultsList.children.length > 0) {
					this.expanded = true;
				}
			}
			this.attachAfterFocusOutCallbacks();
		});
		input.addEventListener('change', () => {
			if (document.activeElement === this.input) { // We fire a fake change event on focusout, so we use this check to skip it
				search(this.input.value);
			}
		});
		input.addEventListener('input', () => {
			search(this.input.value);
		});
	}

	private attachAfterFocusOutCallbacks() {
		if (this.afterFocusOutCallbacks.length) {
			return;
		}
		this.afterFocusOutCallbacks.push(() => this.clearWaitingForResultsSpinner());

		const onInputFocusOut = () => {
			if (document.activeElement !== document.body) {
				this.handleFocusOut(this.allowCustomValue);
			}
		};
		this.input.addEventListener('focusout', onInputFocusOut);
		this.afterFocusOutCallbacks.push(() => this.input.removeEventListener('focusout', onInputFocusOut));
		const onBodyClick = (e: MouseEvent) => {
			if (!this.container.contains(e.target as Node|null) || e.target === this.container) {
				this.handleFocusOut(this.allowCustomValue);
				this.input.blur();
			}
		};
		document.body.addEventListener('click', onBodyClick);
		this.afterFocusOutCallbacks.push(() => document.body.removeEventListener('click', onBodyClick));

		const onBodyFocusIn = () => {
			if (document.activeElement !== this.input) {
				this.handleFocusOut(this.allowCustomValue);
			}
		};
		document.body.addEventListener('focusin', onBodyFocusIn);
		this.afterFocusOutCallbacks.push(() => document.body.removeEventListener('focusin', onBodyFocusIn));
	}

	private handleFocusOut(allowCustomValue: boolean) {
		try {
			this.expanded = false;
			if (!allowCustomValue && this.input.value) {
				for (const item of this.resultsList.querySelectorAll("li[data-value]")) {
					if (item.getAttribute("data-value") === this.input.value) {
						this.input.setCustomValidity("");
						this.input.dispatchEvent(new CustomEvent("change", {bubbles: true, cancelable: true}));
						return;
					}
				}

				this.input.setCustomValidity("Please select a location from the list of options.");
			}
		} finally {
			for (const afterFocusOutCallback of this.afterFocusOutCallbacks) {
				afterFocusOutCallback();
			}
			this.afterFocusOutCallbacks = [];
		}
	}

	private cancelActiveRequest() {
		if (this.abortController !== undefined) {
			this.abortController.abort("NEW_REQUEST");
			this.abortController = undefined;
			this.clearWaitingForResultsSpinner();
		}
	}

	private startWaitingForResults() {
		clearTimeout(this.spinnerDisplayTimeout);
		this.spinnerDisplayTimeout = setTimeout(() => {
			this.container.classList.toggle("awaiting-results", true);
		}, 300);
	}
	private clearWaitingForResultsSpinner() {
		clearTimeout(this.spinnerDisplayTimeout);
		this.container.classList.remove("awaiting-results");
	}

	get expanded() {
		return this.input.ariaExpanded === 'true';
	}
	set expanded(expanded: boolean) {
		const actualExpanded = expanded && document.activeElement === this.input;
		this.input.ariaExpanded = actualExpanded.toString();
		this.container.classList.remove("loading");
		this.resultsList.classList.toggle('is-visible', actualExpanded);
		if (!actualExpanded) {
			this.input.removeAttribute('aria-activedescendant');
			this.resultsCountDisplay.innerHTML = '';
			this.removeHighlight();
		}
	}

	private highlightFirstOption() {
		this.highlightOption(this.resultsList.querySelector<HTMLElement>("li:first-of-type"));
	}
	private highlightLastOption() {
		this.highlightOption(this.resultsList.querySelector<HTMLElement>("li:last-of-type"));
	}

	private highlightPreviousOption() {
		const currentOption = this.resultsList.querySelector('li[aria-selected="true"]');
		if (!currentOption) {
			return this.highlightFirstOption();
		}
		this.removeHighlight();
		this.highlightOption((currentOption.previousElementSibling ?? this.resultsList.lastElementChild) as HTMLElement);
	}
	private highlightNextOption() {
		const currentOption = this.resultsList.querySelector('li[aria-selected="true"]');
		if (!currentOption) {
			return this.highlightFirstOption();
		}
		this.removeHighlight();
		this.highlightOption((currentOption.nextElementSibling ?? this.resultsList.firstElementChild) as HTMLElement);
	}

	private highlightOption(option: HTMLElement|null) {
		if (option) {
			this.input.setAttribute('aria-activedescendant', option.id);
			option.ariaSelected = "true";
		}
	}

	private removeHighlight() {
		for (const option of this.resultsList.querySelectorAll('li')) {
			option.ariaSelected = "false";
		}
	}

	private selectOption(option: HTMLElement|null) {
		if (option) {
			// Set aria-activedescendant on this option's ID
			this.input.removeAttribute('aria-activedescendant');

			// Set the value of the text input to the selected option
			this.input.value = option.getAttribute("data-value") ?? "";
			this.input.setCustomValidity("");
			this.input.dispatchEvent(new Event('change'));

			// Collapse the results
			this.expanded = false;
		}
	}

	public static init(container: HTMLElement, options: AccessibleComboboxOptions) {
		const input = container.querySelector("input");
		const results = container.querySelector<HTMLElement>(".results");
		const resultsCount = container.querySelector<HTMLElement>(".results-count");
		if (input && results && resultsCount) {
			new AccessibleCombobox(container, input, results, resultsCount, options);
		}
	}
}
